import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import FocusTrap from "focus-trap-react";
// vi: filetype=javascript.jsx
import { DDSButton } from "@web-monorepo/dds";
import { UnstyledButton } from "@classdojo/web";
import { LabelText } from "@classdojo/web/nessie";
import { RAW_cssValue } from "@classdojo/web/nessie/stylingLib";
import React from "react";
import { VLayout } from "src/shared/components/FlexLayout";
import { AppMetadata, Tool } from "./EditableSetup";
import EditHeader from "./EditHeader";

type EditableToolEditProps = {
  renderEditItem: ({
    onEdit,
    payload,
    setFocusOnParent,
  }: {
    onEdit: (payload: any, disabled: any) => void;
    payload: any;
    setFocusOnParent?: (focus: boolean) => void;
  }) => React.ReactElement;
  appMetadata: AppMetadata;

  closeEditScreen: VoidFunction;
  createTool: (payload: Record<string, unknown>) => void;
  editTool: (toolId: string, payload: Record<string, unknown>) => void;
  deleteTool: (toolId?: any) => void;
  showTool: (payload: any) => void;
  tool: Tool | null;

  focusOnParent?: boolean;
  setFocusOnParent?: (focus: boolean) => void;
};

type EditableToolEditState = {
  disabled: boolean;
  payload: Record<string, unknown>;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class EditableToolEdit extends React.Component<EditableToolEditProps, EditableToolEditState> {
  constructor(props: EditableToolEditProps) {
    super(props);

    if (props.tool) {
      this.state = {
        payload: props.tool.payload,
        disabled: false,
      };
    } else {
      this.state = {
        disabled: true,
        payload: {},
      };
    }
  }

  render() {
    return (
      <FocusTrap active={this.props.focusOnParent}>
        <VLayout sx={{ padding: RAW_cssValue("18px") }} gutter={1}>
          <EditHeader
            title={this.props.tool ? autoTranslate("Edit") : autoTranslate("New")}
            close={this.props.closeEditScreen}
          />
          {this.props.renderEditItem({
            onEdit: this.onEdit,
            payload: this.state.payload,
            setFocusOnParent: this.props.setFocusOnParent,
          })}

          <DDSButton
            data-name="editable_tool_edit:render:other_actions.start_now"
            disabled={this.state.disabled || !this.state.payload}
            onClick={this.startNow}
            sx={{
              width: "100%",
              color: RAW_cssValue(this.props.appMetadata.textColor ?? "dt_content_light"),
              "&, &:hover": {
                backgroundColor: RAW_cssValue(this.props.appMetadata.backgroundColor),
                span: { color: RAW_cssValue(this.props.appMetadata.textColor ?? "dt_content_light") },
              },
              "&:hover": {
                opacity: 0.7,
              },
            }}
          >
            Start now
          </DDSButton>
          <DDSButton
            data-name="editable_tool_edit:render:other_actions.save_for_later"
            disabled={this.state.disabled}
            onClick={this.saveForLater}
            variant="secondary"
            sx={{
              width: "100%",
              "&, &:hover": {
                backgroundColor: "dt_background_primary",
                border: RAW_cssValue("1px solid"),
                borderColor: "dt_content_primary",
                span: { color: "dt_content_primary" },
              },
              "&:hover": {
                opacity: 0.7,
              },
            }}
          >
            Save for later
          </DDSButton>
          {this.props.tool && this.props.tool._id ? (
            <UnstyledButton
              data-name="editable_tool_edit:render:other_actions_delete"
              sx={{ width: "100%", textAlign: "center" }}
              onClick={this.delete}
            >
              <LabelText kind="danger">Delete</LabelText>
            </UnstyledButton>
          ) : null}
        </VLayout>
      </FocusTrap>
    );
  }

  startNow = () => {
    this.save();
    this.props.showTool(this.state.payload);
  };

  saveForLater = () => {
    this.save();
    this.props.closeEditScreen();
  };

  save = () => {
    if (this.props.tool && this.props.tool._id) {
      this.props.editTool(this.props.tool._id, this.state.payload);
    } else if (this.state.payload) {
      this.props.createTool(this.state.payload);
    }
  };

  onEdit = (payload: Record<string, unknown>, disabled: boolean) => {
    this.setState({ payload, disabled });
  };

  delete = () => {
    this.props.deleteTool(this.props.tool?._id);
  };
}
